import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { ApplicationService } from "../../../../services/ApplicationService";
import { CompanyService } from "../../../../services/CompanyService";
import { usePermissions } from "../../../../PermissionsProvider";

interface BackendSaveMetadata {
  conversation_flow_name: string;
  company_id: string;
  application_ref_name: string;
  action_type: string;
  conversation_flow_version: number; // Always 1 for new trees
  is_latest: boolean; // Always true for new trees
}

interface SaveTreeModalProps {
  visible: boolean;
  onSave: (metadata: BackendSaveMetadata) => void;
  onCancel: () => void;
}

interface Application {
  id: number;
  application_name: string;
  application_ref_name: string;
}

interface Intent {
  id: number;
  action_type: string;
  description?: string;
}

const SaveTreeModal: React.FC<SaveTreeModalProps> = ({
  visible,
  onSave,
  onCancel,
}) => {
  
  const { companyId, apiKey } = usePermissions();
  const [applications, setApplications] = useState<Application[]>([]);
  const [intents, setIntents] = useState<Intent[]>([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | undefined>();
  const [selectedIntentId, setSelectedIntentId] = useState<number | undefined>();
  const [conversationFlowName, setConversationFlowName] = useState("");
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [loadingIntents, setLoadingIntents] = useState(false);

  // Load applications when modal opens
  useEffect(() => {
    if (visible && companyId && apiKey) {
      setLoadingApplications(true);
      const response = CompanyService.getApplicationsByCompany(companyId, null, apiKey);
      
      const subscription = response.subscribe({
        next: (apps: Application[]) => {
          setApplications(apps);
          setLoadingApplications(false);
        },
        error: (error: any) => {
          toast.error("Failed to load applications");
          setLoadingApplications(false);
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [visible, companyId, apiKey]);

  // Load intents when application is selected
  useEffect(() => {
    if (selectedApplicationId && companyId && apiKey) {
      setLoadingIntents(true);
      setIntents([]);
      
      const response = ApplicationService.getApplicationActions(selectedApplicationId, null, apiKey);
      
      const subscription = response.subscribe({
        next: (intentData: Intent[]) => {
          setIntents(intentData);
          setLoadingIntents(false);
        },
        error: (error: any) => {
          toast.error("Failed to load intents");
          setLoadingIntents(false);
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [selectedApplicationId, companyId, apiKey]);

  const handleApplicationChange = (applicationId: string) => {
    const appId = parseInt(applicationId);
    setSelectedApplicationId(appId);
    setSelectedIntentId(undefined); // Reset intent selection
  };

  const handleOk = () => {
    // Find the selected application and intent
    const selectedApp = applications.find(app => app.id === selectedApplicationId);
    const selectedIntent = intents.find(intent => intent.id === selectedIntentId);
    
    if (!selectedApp || !selectedIntent || !conversationFlowName.trim()) {
      toast.error("Please select both application and intent, and provide a tree name");
      return;
    }

    const metadata: BackendSaveMetadata = {
      conversation_flow_name: conversationFlowName,
      company_id: String(companyId),
      application_ref_name: selectedApp.application_ref_name || selectedApp.application_name,
        action_type: selectedIntent.action_type,
        conversation_flow_version: 1, // Always version 1 for new trees
        is_latest: true // Always latest for new trees
      };

    onSave(metadata);
    setConversationFlowName("");
    setSelectedApplicationId(undefined);
    setSelectedIntentId(undefined);
    setIntents([]);
  };

  const handleCancel = () => {
    onCancel();
    setConversationFlowName("");
    setSelectedApplicationId(undefined);
    setSelectedIntentId(undefined);
    setIntents([]);
  };

  return (
    <Dialog open={visible} onOpenChange={(open) => { if (!open) handleCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            Save Conversation Tree
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tree-name" className="text-sm font-gc-med text-foreground">
              Tree Name
            </Label>
            <Input
              id="tree-name"
              placeholder="Enter a descriptive name for your conversation tree"
              value={conversationFlowName}
              onChange={(e) => setConversationFlowName(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="application" className="text-sm font-gc-med text-foreground">
              Application
            </Label>
            <Select value={selectedApplicationId?.toString()} onValueChange={handleApplicationChange}>
              <SelectTrigger>
                <SelectValue placeholder={loadingApplications ? "Loading applications..." : "Select an application"} />
              </SelectTrigger>
              <SelectContent>
                {applications.map((app) => (
                  <SelectItem key={app.id} value={app.id.toString()}>
                    {app.application_name || app.application_ref_name || `App ${app.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="intent" className="text-sm font-gc-med text-foreground">
              Intent
            </Label>
            <Select 
              value={selectedIntentId?.toString()} 
              onValueChange={(value) => setSelectedIntentId(parseInt(value))}
              disabled={!selectedApplicationId}
            >
              <SelectTrigger>
                <SelectValue 
                  placeholder={
                    !selectedApplicationId 
                      ? "Select an application first" 
                      : loadingIntents 
                      ? "Loading intents..." 
                      : "Select an intent"
                  } 
                />
              </SelectTrigger>
              <SelectContent>
                {intents.map((intent) => (
                  <SelectItem key={intent.id} value={intent.id.toString()}>
                    <div>
                      <span>{intent.action_type}</span>
                      {intent.description && (
                        <span className="text-xs text-muted-foreground ml-2">
                          - {intent.description}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleOk}
            disabled={!conversationFlowName.trim() || !selectedApplicationId || !selectedIntentId}
          >
            Save Tree
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveTreeModal; 